import React, { useEffect, useState } from "react";
import { Base } from "../../framework/common/base";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { commonBoxStyle, MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "../framework/loadingButton";
import { MuiDateTimePicker } from "../framework/muiDateTimePicker";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { fetchWorkTimeTypes, saveOverwritingBreak } from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { addSuccessMsg } from "../../framework/redux/slices/appSlice";
import { handleApiError } from "../../services/baseService";
import { NonOverlappingWorkShiftTimeSlotSaveData } from "../../models/workShiftTimeSlot/nonOverlappingWorkShiftTimeSlotSaveData";
import { IWorkTimeType } from "../../models/workShiftTimeSlot/workTimeType";
import { optionWorkTimeBreakButtonInMobileEnabled } from "../../models/common/consts";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import MuiSelect from "../framework/muiSelect";
import { useWorkTimeDefaultTimezone } from "../../hooks/framework/useWorkTimeDefaultTimezone";

export const WorkTimeBreakForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { date } = useParams();
    const employeeId = useAppSelector((state) => state.session.id);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [breakDuration, setBreakDuration] = useState<string>("30");
    const [startDateTime, setStartDateTime] = useState<string | null>(null);
    const [selectedWorkTimeTypeId, setSelectedWorkTimeTypeId] = useState<string | null>(null);
    const [breakSaveData, setBreakSaveData] = useState<NonOverlappingWorkShiftTimeSlotSaveData>();
    const [breakTypes, setBreakTypes] = useState<IWorkTimeType[]>([]);
    // Break duration option items
    const breaks = ["5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60"];
    const [defaultTz] = useWorkTimeDefaultTimezone();

    const [workTimeBreakButtonInMobileEnabled] = useAppOptionValueEquals(
        optionWorkTimeBreakButtonInMobileEnabled,
        true
    );

    const handleSubmit = async () => {
        setIsSaving(true);
        await dispatch(saveOverwritingBreak(breakSaveData))
            .unwrap()
            .then(() => {
                dispatch(addSuccessMsg(t("workTime.workTimeSaveSuccessful")));
                goBack();
            })
            .catch(handleApiError)
            .finally(() => {
                setIsSaving(false);
            });
    };
    const goBack = () => {
        navigate(generatePath("/worklog/:date?", { date }));
    };

    const handleBreakChange = (id: string) => {
        setSelectedWorkTimeTypeId(id);
    };
    const handleDurationChange = (duration: string) => {
        setBreakDuration(duration);
    };

    useEffect(() => {
        if (date) {
            setStartDateTime(date + "T12:00:00");
        }
    }, [date]);

    const workTimeTypes = useAppSelector(
        ({ workShiftTimeSlot }) => workShiftTimeSlot.workTimeTypes
    );
    useEffect(() => {
        const promise = dispatch(fetchWorkTimeTypes());
        return () => {
            promise.abort();
        };
    }, []);

    useEffect(() => {
        if (workTimeTypes) {
            const breaks = workTimeTypes.filter(i => i.type === 0);
            if (breaks.length === 1) {
                setSelectedWorkTimeTypeId(breaks[0].workTimeTypeId);
            }
            setBreakTypes(breaks);
        }
    }, [workTimeTypes]);

    useEffect(() => {
        setBreakSaveData({
            employeeId: employeeId,
            startDate: startDateTime,
            endDate: Base.dateToJsonDateTime(
                Base.dateAddMinutes(startDateTime, Number(breakDuration))
            ),
            workTimeTypeId: selectedWorkTimeTypeId,
            timeZoneName: defaultTz,
        });
    }, [breakDuration, startDateTime, selectedWorkTimeTypeId, defaultTz]);

    return (
        <MainLayout
            hasPx
            topComponent={
                <PageTitle
                    header={`${t("workTime.addBreak")}`}
                />
            }
        >
            <Box sx={commonBoxStyle} mb={2}>
                <MuiDateTimePicker
                    dateLabel={t("time.startDate")}
                    timeLabel={t("time.startTime")}
                    onChange={(value) =>
                        setStartDateTime(value as string)
                    }
                    value={startDateTime}
                    required
                    asUtc={false}
                    dateDisabled={true}
                />
            </Box>
            <Box sx={commonBoxStyle} mb={2}>
                <Box minWidth={200}>
                    <MuiSelect
                        label={t("common.break")}
                        value={selectedWorkTimeTypeId}
                        name="breakType"
                        options={(breakTypes || []).map((i) => ({
                            id: i.workTimeTypeId,
                            title: i.name,
                        }))}
                        onChange={(event) => handleBreakChange(event.target.value)}
                        disabled={breakTypes.length <= 1}
                        required
                    />
                </Box>
            </Box>
            <Box sx={commonBoxStyle} mb={2}>
                <Box minWidth={120}>
                    <MuiSelect
                        label={t("time.duration")}
                        value={breakDuration}
                        name="breakDuration"
                        options={(breaks || []).map((i) => ({
                            id: i,
                            title: i + " " + t("time.abrMins"),
                        }))}
                        onChange={(event) => handleDurationChange(event.target.value)}
                    />
                </Box>
            </Box>
            <Box sx={commonBoxStyle}>
                <Button
                    onClick={() => void goBack()}
                    variant="outlined"
                    color="success"
                >
                    {t("common.cancel")}
                </Button>
                <LoadingButton
                    onClick={() => void handleSubmit()}
                    variant="contained"
                    color="success"
                    disabled={!selectedWorkTimeTypeId || !workTimeBreakButtonInMobileEnabled}
                    loading={isSaving}
                >
                    {t("common.save")}
                </LoadingButton>
            </Box>
        </MainLayout>
    );
};
